import React, { useState, useCallback, useEffect } from 'react';
import { Spin, Card, Tabs, Typography, message } from 'antd';
import './PWH205Form.css';

import { usePWH205Mode } from './hooks/usePWH205Mode';
import { usePWH205Record } from './hooks/usePWH205Record';
import { usePWH205Navigation } from './hooks/usePWH205Navigation';
import ActionBar from './components/ActionBar';
import Tab1Header from './components/Tab1Header';
import DetailGrid from './components/DetailGrid';
import { lookupApi } from '../../services/lookupApi';
import { statusLabel } from './helpers';
import { pwh205Api } from '../../services/pwh205Api';
import type { FH110, FH111 } from '../../services/pwh205Api';
import { auditLogApi } from '../../services/auditLogApi';
import {
  AcceptanceDataPanel,
  AcceptanceFieldHint,
  AcceptanceToolbar,
  pwh205f1AcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';

const { Text } = Typography;

const EMPTY_MASTER: Partial<FH110> = {
  fh110_01: '', fh110_11: new Date().toISOString(),
  fh110_12: 0, fh110_13: 0, fh110_14: 'N', fh110_15: '', fh110_16: '',
  fh110_17: '', fh110_22: '8', fh110_23: null,
  fh110_241: false, fh110_242: false, fh110_25: 'NTD', fh110_26: 1,
  fh110_27: '', fh110_28: 0, fh110_29: null, fh110_30: null,
  fh110_31: 0, fh110_32: 0, fh110_37: '', fh110_39: 0,
  fh110_42: false, fh110_43: '', fh110_44: false,
  fh110_46: '', fh110_47: '', fh110_50: 1, fh110_80: 'N',
};

const PWH205Form: React.FC = () => {
  const { mode, toView, toAdd, toEdit, isEditing } = usePWH205Mode();
  const { record, loading, saving, load, save, remove } = usePWH205Record();
  const { hasPrev, hasNext, fetchList, goFirst, goLast, goPrev, goNext, setCurrentById } =
    usePWH205Navigation();

  const [formData, setFormData] = useState<Partial<FH110>>({ ...EMPTY_MASTER });
  const [details, setDetails] = useState<Partial<FH111>[]>([]);
  const [activeTab, setActiveTab] = useState('1');
  const [acceptanceLatest, setAcceptanceLatest] = useState<FH110[]>([]);
  const [acceptanceDatabaseRecord, setAcceptanceDatabaseRecord] = useState<FH110 | null>(null);
  const [acceptanceAudit, setAcceptanceAudit] = useState<unknown[]>([]);
  const [acceptanceErrors, setAcceptanceErrors] = useState<string[]>([]);
  const [acceptanceLoading, setAcceptanceLoading] = useState(false);
  const acceptanceMode = useAcceptanceMode();
  const acceptanceEnabled = acceptanceMode.isEnabled(pwh205f1AcceptanceManifest.formId);

  // Lookup names
  const [companyName, setCompanyName] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [deptName, setDeptName] = useState('');
  const [currencyName, setCurrencyName] = useState('');
  const [expenseNames, setExpenseNames] = useState<Record<string, string>>({});

  // Sync record -> form
  useEffect(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    }
  }, [record]);

  // Resolve all lookup names
  const resolveNames = useCallback(async (data: Partial<FH110>, dets: Partial<FH111>[]) => {
    const resolveOne = async (type: string, code: string | undefined) => {
      if (!code?.trim()) return '';
      try { const r = await lookupApi.getName(type, code); return r.name || ''; }
      catch { return '*查無此代號*'; }
    };
    const [co, pay, dept, cur] = await Promise.all([
      resolveOne('supplier', data.fh110_16),
      resolveOne('supplier', data.fh110_15),
      resolveOne('department', data.fh110_37),
      resolveOne('currency', data.fh110_25),
    ]);
    setCompanyName(co); setPayeeName(pay); setDeptName(dept); setCurrencyName(cur);

    const codes = Array.from(new Set(dets.map(d => d.fh111_19).filter(Boolean)));
    const nameMap: Record<string, string> = {};
    await Promise.all(codes.map(async (c) => {
      if (c) {
        try { const r = await lookupApi.getName('expenseCode', c); nameMap[c] = r.name || ''; }
        catch { nameMap[c] = '*查無此代號*'; }
      }
    }));
    setExpenseNames(nameMap);
  }, []);

  const loadRecord = useCallback(async (id: string) => {
    const result = await load(id);
    if (result) { setCurrentById(id); await resolveNames(result, result.details || []); }
    return result;
  }, [load, setCurrentById, resolveNames]);

  // Navigation
  const handleFirst = useCallback(async () => { const id = goFirst(); if (id) await loadRecord(id); }, [goFirst, loadRecord]);
  const handlePrev = useCallback(async () => { const id = goPrev(); if (id) await loadRecord(id); }, [goPrev, loadRecord]);
  const handleNext = useCallback(async () => { const id = goNext(); if (id) await loadRecord(id); }, [goNext, loadRecord]);
  const handleLast = useCallback(async () => { const id = goLast(); if (id) await loadRecord(id); }, [goLast, loadRecord]);

  // Mode transitions
  const handleAdd = useCallback(() => {
    setFormData({ ...EMPTY_MASTER, fh110_11: new Date().toISOString() });
    setDetails([]);
    setCompanyName(''); setPayeeName(''); setDeptName(''); setCurrencyName(''); setExpenseNames({});
    toAdd();
  }, [toAdd]);

  const handleEdit = useCallback(() => { toEdit(); }, [toEdit]);

  const handleCancel = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
      resolveNames(record, record.details || []);
    } else { setFormData({ ...EMPTY_MASTER }); setDetails([]); }
    toView();
  }, [record, toView, resolveNames]);

  // BR-C01: calculated amounts
  const netAmount = details.reduce((s, d) => s + ((d.fh111_15 as number) || 0), 0);
  const totalAmount = Math.round((netAmount + ((formData.fh110_28 as number) || 0)) * 100) / 100;

  const handleSave = useCallback(async () => {
    const masterToSave: Partial<FH110> = {
      ...formData, fh110_12: netAmount, fh110_13: totalAmount, fh110_39: details.length,
    };
    const result = await save(mode as 'add' | 'edit', masterToSave, details);
    if (result) {
      toView(); await fetchList(); setCurrentById(result.fh110_01);
      await resolveNames(result, result.details || []);
    }
  }, [formData, details, netAmount, totalAmount, mode, save, toView, fetchList, setCurrentById, resolveNames]);

  const handleDelete = useCallback(async () => {
    if (!record) return;
    const ok = await remove(record.fh110_01);
    if (ok) { toView(); await fetchList(); setFormData({ ...EMPTY_MASTER }); setDetails([]); }
  }, [record, remove, toView, fetchList]);

  // BR-F12: Submit/Retract approval
  const handleSubmitApproval = useCallback(async () => {
    if (!record) return;
    try {
      if (record.fh110_80 === 'D') {
        await pwh205Api.withdraw(record.fh110_01);
        message.success('已撤回為草稿');
      } else {
        await pwh205Api.submit(record.fh110_01);
        message.success('已送審');
      }
      await fetchList();
      await loadRecord(record.fh110_01);
    } catch {
      message.error('送審狀態更新失敗');
    }
  }, [record, fetchList, loadRecord]);

  const handleApprove = useCallback(async () => {
    if (!record) return;
    try {
      await pwh205Api.approve(record.fh110_01);
      message.success('已核准');
      await fetchList();
      await loadRecord(record.fh110_01);
    } catch {
      message.error('核准失敗；請確認您有核准權限且單據仍為送審中');
    }
  }, [record, fetchList, loadRecord]);

  const handleViewHistory = useCallback(() => { message.info('審核歷史功能尚未實作'); }, []);

  const handleFieldChange = useCallback(<K extends keyof FH110>(field: K, value: FH110[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Lookup onBlur handlers
  const handleCompanyBlur = useCallback(async () => {
    const code = (formData.fh110_16 || '').trim();
    if (!code) { setCompanyName(''); return; }
    try {
      const r = await lookupApi.getName('supplier', code);
      setCompanyName(r.name || '');
      // BR-F13: auto-fill payee = company
      setFormData(prev => ({ ...prev, fh110_15: code }));
      setPayeeName(r.name || '');
    } catch { setCompanyName('*查無此代號*'); }
  }, [formData.fh110_16]);

  const handlePayeeBlur = useCallback(async () => {
    const code = (formData.fh110_15 || '').trim();
    if (!code) { setPayeeName(''); return; }
    try { const r = await lookupApi.getName('supplier', code); setPayeeName(r.name || ''); }
    catch { setPayeeName('*查無此代號*'); }
  }, [formData.fh110_15]);

  const handleDeptBlur = useCallback(async () => {
    const code = (formData.fh110_37 || '').trim();
    if (!code) { setDeptName(''); return; }
    try { const r = await lookupApi.getName('department', code); setDeptName(r.name || ''); }
    catch { setDeptName('*查無此代號*'); }
  }, [formData.fh110_37]);

  const handleCurrencyBlur = useCallback(async () => {
    const code = (formData.fh110_25 || '').trim();
    if (!code) { setCurrencyName(''); return; }
    if (code.substring(0, 2).toUpperCase() === 'NT') {
      setFormData(prev => ({ ...prev, fh110_26: 1 }));
    }
    try { const r = await lookupApi.getName('currency', code); setCurrencyName(r.name || ''); }
    catch { setCurrencyName('*查無此代號*'); }
  }, [formData.fh110_25]);

  const handleExpenseBlur = useCallback(async (code: string) => {
    if (!code.trim()) return;
    try { const r = await lookupApi.getName('expenseCode', code); setExpenseNames(prev => ({ ...prev, [code]: r.name || '' })); }
    catch { setExpenseNames(prev => ({ ...prev, [code]: '*查無此代號*' })); }
  }, []);

  const loadAcceptanceData = useCallback(async () => {
    if (!acceptanceEnabled) return;
    setAcceptanceLoading(true);
    const [latest, database, audit] = await Promise.allSettled([
      pwh205Api.list(1, 3),
      record?.fh110_01 ? pwh205Api.get(record.fh110_01) : Promise.resolve(null),
      auditLogApi.list({
        form_id: pwh205f1AcceptanceManifest.formId,
        doc_key: record?.fh110_01,
        page: 1,
        pageSize: 20,
      }),
    ]);
    setAcceptanceLatest(latest.status === 'fulfilled' ? latest.value.data || [] : []);
    setAcceptanceDatabaseRecord(database.status === 'fulfilled' ? database.value : null);
    setAcceptanceAudit(audit.status === 'fulfilled' ? audit.value.data || [] : []);
    setAcceptanceErrors([
      ...(latest.status === 'rejected' ? ['最新三筆 API 讀取失敗。'] : []),
      ...(database.status === 'rejected' ? ['目前單據 API 讀取失敗。'] : []),
      ...(audit.status === 'rejected' ? ['SYS_AuditLog 讀取失敗；目前不可把留痕視為已確認。'] : []),
    ]);
    setAcceptanceLoading(false);
  }, [acceptanceEnabled, record?.fh110_01]);

  useEffect(() => {
    void loadAcceptanceData();
  }, [loadAcceptanceData]);

  const handleAcceptanceAutoFill = useCallback(() => {
    if (!record) {
      message.warning('請先開啟一張既有預付款申請，系統才有真實代號可作為安全範例。');
      return;
    }
    const today = new Date();
    const writeoffDate = new Date(today);
    writeoffDate.setDate(writeoffDate.getDate() + 30);
    setFormData({
      ...record,
      fh110_01: '',
      fh110_11: today.toISOString(),
      fh110_23: writeoffDate.toISOString(),
      fh110_29: writeoffDate.toISOString(),
      fh110_80: 'N',
      fh110_90: 0,
      fh110_91: '',
      fh110_92: null,
    });
    setDetails((record.details || []).map((item, index) => ({
      ...item,
      fh111_01: '',
      fh111_03: index + 1,
      fh111_90: 0,
    })));
    setActiveTab('1');
    toAdd();
    message.success('已產生驗收草稿；尚未寫入資料庫，按正式儲存後仍會走原本 API、權限與驗證。');
  }, [record, toAdd]);

  // Load initial list
  useEffect(() => { fetchList(); }, [fetchList]);

  const displayStatus = statusLabel(formData.fh110_80 || '', formData.fh110_20, formData.fh110_51);

  return (
    <div className="pwh205-form">
      <ActionBar
        mode={mode} hasRecord={!!record} hasPrev={hasPrev} hasNext={hasNext}
        saving={saving} canAdd canEdit canDelete
        approvalStatus={formData.fh110_80 || 'N'}
        onFirst={handleFirst} onPrev={handlePrev} onNext={handleNext} onLast={handleLast}
        onAdd={handleAdd} onEdit={handleEdit} onSave={handleSave}
        onCancel={handleCancel} onDelete={handleDelete}
        onSubmitApproval={handleSubmitApproval} onApprove={handleApprove} onViewHistory={handleViewHistory}
      />
      <AcceptanceToolbar
        manifest={pwh205f1AcceptanceManifest}
        onAutoFill={handleAcceptanceAutoFill}
        documentKey={record?.fh110_01}
        autoFillDisabled={!record || isEditing}
        autoFillReason={!record
          ? '請先開啟一張既有預付款申請，作為只讀範例來源。'
          : isEditing
            ? '請先結束目前編輯，再產生新的驗收草稿。'
            : '只複製現有資料成畫面草稿；儲存仍走正式 PWH205 API。'}
      />
      <Spin spinning={loading}>
        <Card title="暫借款請款單" className="pwh205-main-card"
          extra={displayStatus && <Text type="danger" strong className="status-tag">
            {displayStatus}
            <AcceptanceFieldHint
              manifest={pwh205f1AcceptanceManifest}
              fieldId="fh110_80"
              documentKey={record?.fh110_01}
            />
          </Text>}>
          <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
            { key: '1', label: '單頭', children: (
              <Tab1Header
                formData={formData} isEditing={isEditing}
                netAmount={netAmount} totalAmount={totalAmount}
                companyName={companyName} payeeName={payeeName} deptName={deptName} currencyName={currencyName}
                documentKey={record?.fh110_01}
                onFieldChange={handleFieldChange}
                onCompanyBlur={handleCompanyBlur} onPayeeBlur={handlePayeeBlur}
                onDeptBlur={handleDeptBlur} onCurrencyBlur={handleCurrencyBlur}
              />
            )},
            { key: '2', label: '明細', children: (
              <DetailGrid details={details} isEditing={isEditing} onChange={setDetails}
                expenseNames={expenseNames} onExpenseBlur={handleExpenseBlur}
                detailCount={details.length}
                paidTotal={formData.fh110_21 ?? 0}
                writeOffRecords={record?.write_off_records || []}
                documentKey={record?.fh110_01}
              />
            )},
          ]} />
        </Card>
      </Spin>
      <AcceptanceDataPanel
        manifest={pwh205f1AcceptanceManifest}
        documentKey={record?.fh110_01}
        snapshots={{
          screenDraft: { master: formData, details },
          lastApiResponse: record ? { master: record, details: record.details || [] } : null,
          databaseRecord: acceptanceDatabaseRecord
            ? { master: acceptanceDatabaseRecord, details: acceptanceDatabaseRecord.details || [] }
            : null,
        }}
        latestRecords={acceptanceLatest}
        history={record?.audit_records || []}
        auditEntries={acceptanceAudit}
        loading={acceptanceLoading || loading}
        onRefresh={() => { void loadAcceptanceData(); }}
        observationErrors={acceptanceErrors}
      />
    </div>
  );
};

export default PWH205Form;
